import React, { useEffect, useState } from "react";
import {
  Page,
  Card,
  Text,
  Box,
  Button,
  TextField,
  InlineStack,
  ProgressBar,
} from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SetupTabs from "./SetupTabs";
import { useContent } from "../Provider/ContentContext";

const USER_MESSAGES = {
  urlSpaces: "Product URL cannot contain spaces.",
  localhost:
    "Local/test URLs are not allowed. Please enter a public product URL.",
  invalidUrl: "Please enter a valid product URL starting with https://",
  noSuggestions:
    "We couldn’t generate title suggestions right now. Please try again.",
  regenerateFailed:
    "We couldn’t generate ideas right now. Please check your details and try again.",
};

function GenericProductInput({ shopOrigin, apiKey }) {
  const navigate = useNavigate();
  const { contentData, setContentData } = useContent();

  const [keywords, setKeywords] = useState("");
  const [productUrl, setProductUrl] = useState("");
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [step, setStep] = useState(2);
  const [progress, setProgress] = useState(0);
  const [progressVisible, setProgressVisible] = useState(false);

  const [mobileViewHeight, setMobileViewHeight] = useState(0);

  useEffect(() => {
      const updateHeight = () => {
        if (window.innerWidth <= 768) {
          setMobileViewHeight(window.innerHeight);
        }
      };

      updateHeight();
      window.addEventListener('resize', updateHeight);
      window.addEventListener('orientationchange', updateHeight);

      return () => {
        window.removeEventListener('resize', updateHeight);
        window.removeEventListener('orientationchange', updateHeight);
      };
    }, []);

    // Then apply dynamic styles to your tone-radio div
    const mobileStyles = window.innerWidth <= 768 ? {
      position: 'sticky',
      bottom: 0,
      left: 0,
      width: '100%',
      zIndex: 1000,
      backgroundColor: '#F1F1F1',
      // boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
      padding: '15px 12px',
      margin: 0
  } : {};

  // Regex
  const urlRegex = /^https:\/\/[^\s/$.?#].[^\s]*$/i;
  const localhostRegex = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d{1,5})?/i;

  const handleUrlValidation = (value) => {
    const newErrors = { ...errors };
    if (value.trim() === "") {
      delete newErrors.productUrl;
    } else if (/\s/.test(value)) {
      newErrors.productUrl = USER_MESSAGES.urlSpaces;
    } else if (localhostRegex.test(value)) {
      newErrors.productUrl = USER_MESSAGES.localhost;
    } else if (!value.startsWith("https://")) {
      newErrors.productUrl = USER_MESSAGES.invalidUrl;
    } else if (!urlRegex.test(value)) {
      newErrors.productUrl = USER_MESSAGES.invalidUrl;
    } else {
      delete newErrors.productUrl;
    }
    setErrors(newErrors);
  };

  const handleProductUrlChange = (value) => {
    setProductUrl(value);
    handleUrlValidation(value);
  };

  function startProgressBar(onComplete) {
    setProgressVisible(true);
    let progressValue = 0;
    const animatedDuration = 30000; // 30 seconds
    const intervalTime = 50;
    const startTime = Date.now();

    const interval = setInterval(() => {
      const elapsedTime = Date.now() - startTime;
      progressValue = Math.min(98, (elapsedTime / animatedDuration) * 98);
      setProgress(progressValue);

      if (progressValue >= 98 || elapsedTime >= animatedDuration) {
        clearInterval(interval);
        setProgress(98);
      }
    }, intervalTime);

    return () => {
      clearInterval(interval);
      setProgress(100);
      setTimeout(() => {
        setProgressVisible(false);
        onComplete && onComplete();
      }, 500);
    };
  }

  const handleContinue = async () => {
    if ((!keywords.trim() && !productUrl.trim()) || errors.productUrl) return;

    setContentData((prev) => ({
      ...prev,
      keywords,
      product_url: productUrl,
      content_type: "generic_product",
    }));

    // Start progress bar like in ArticleSuggestions
    const completeProgressBar = startProgressBar(() => {
      setProgressVisible(false);
    });

    const payload = {
      shop: shopOrigin || "",
      keywords,
      product_url: productUrl,
      content_type: "generic_product",
      tone: contentData.tone || "friendly",
      language: contentData.language || "English",
    };

    try {
      setError("");
      // Save tone/content
      await axios.post("/api/save-tone-content/", payload, {
        headers: { "Content-Type": "application/json" },
      });

      // Store combined content
      const combinedPayload = { ...contentData, ...payload };
      await axios.post("/api/store-content/", combinedPayload, {
          headers: { "Content-Type": "application/json" },
      });

      // Regenerate titles (same as ArticleSuggestions)
      const response = await axios.get(
        `/api/regenerate-titles/${encodeURIComponent(shopOrigin || "")}/`,
        {
          params: {
            content_type: "generic_product",
            keywords: keywords || "",
            product_url: productUrl || "",
            tone: contentData.tone || "friendly",
            language: contentData.language || "English",
          },
          headers: { "Content-Type": "application/json" },
        }
      );

      if (completeProgressBar) completeProgressBar();

      if (response.data.status === "success" && response.data.title_teasers) {
        navigate(`/regenerate-title-combos?shop=${encodeURIComponent(shopOrigin || "")}`, {
          state: { titleTeasers: response.data.title_teasers },
        });
      } else {
        setError(response.data.message || USER_MESSAGES.noSuggestions);
      }
    } catch (err) {
      console.error("Error regenerating titles:", err.response?.data || err.message);
      setError(err.response?.data?.message || USER_MESSAGES.regenerateFailed);
      if (completeProgressBar) completeProgressBar();
    }
  };

  return (
    <Page title="BlogHero Setup">
      <SetupTabs step={step} setStep={setStep} />
      <Card padding={{ xs: "600", sm: "800" }} sectioned>
        {error && (
          <Box paddingBlockEnd="300">
            <Text tone="critical">{error}</Text>
          </Box>
        )}
        <Box paddingBlockEnd="400">
          <Text variant="headingSm" as="h2">
            I want to create product-specific content
          </Text>
          <Text variant="bodyMd" as="p">
            Tell us about the product you want to generate content for
          </Text>
        </Box>

        <Box paddingBlockEnd="400">
          <TextField
            label="Type keywords related to the product you want to generate content for"
            value={keywords}
            onChange={setKeywords}
            placeholder="e.g., Organic lipstick, face serum"
          />
        </Box>

        <Box paddingBlockEnd="400">
          <TextField
            label="Paste a URL of your product"
            value={productUrl}
            onChange={handleProductUrlChange}
            placeholder="e.g. https://yourstore.myshopify.com/products/organic-lipsticks"
            error={errors.productUrl}
          />
        </Box>

        {progressVisible && (
          <Box>
            <ProgressBar progress={progress} size="small" />
          </Box>
        )}
      </Card>

      <div style={{ marginTop: "15px", marginLeft: "24px", marginRight:"24px", ...mobileStyles  }} className="tone-radio">
        <InlineStack align="space-between">
          <Button
            size="medium"
            onClick={() =>
              navigate(
                `/content-setup?shop=${encodeURIComponent(shopOrigin || "")}`
              )
            }
          >
            Back
          </Button>
          <Button
            size="medium"
            variant="primary"
            disabled={
              (!keywords.trim() && !productUrl.trim()) ||
              !!errors.productUrl
            }
            onClick={handleContinue}
          >
            Generate Suggestions
          </Button>

        </InlineStack>
      </div>
    </Page>
  );
}

export default GenericProductInput;
